import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import type {
  GitBranchFromCurrentResponse,
  GitCommit,
  GitJumpResponse,
  GitPublishResponse,
  GitReturnResponse,
  GitSaveResponse,
  GitState,
} from '@claude-code-api/shared';
import type { ChatSessionMeta } from '@/components/talk/types';
import { useTalkSessionStore } from '@/components/talk/useTalkSessionStore';
import type { TalkSessionActions, TalkSessionDataState } from './types';

function getToastMethod(status: string) {
  if (status === 'error' || status === 'conflict_restore_failed')
    return toast.error;
  if (
    status === 'blocked_dirty_state' ||
    status === 'blocked_missing_upstream' ||
    status === 'travel_active'
  )
    return toast.warning;
  if (status === 'no_op') return toast.info;
  return toast.success;
}

function resolveCreatedChatSessionId(
  payload: unknown,
  existingIds: Set<string>,
): string | null {
  if (!payload || typeof payload !== 'object') return null;

  const record = payload as Record<string, unknown>;
  const directCandidates = [
    record.activeSessionId,
    record.id,
    (record.session as { id?: unknown } | undefined)?.id,
    (record.chatSession as { id?: unknown } | undefined)?.id,
    (record.data as { id?: unknown } | undefined)?.id,
  ];

  for (const candidate of directCandidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate;
    }
  }

  const sessions = Array.isArray(record.sessions)
    ? record.sessions
    : Array.isArray(
          (record.data as { sessions?: unknown } | undefined)?.sessions,
        )
      ? ((record.data as { sessions?: unknown }).sessions as unknown[])
      : [];

  for (const entry of sessions) {
    const id =
      typeof entry === 'object' && entry && 'id' in entry
        ? String((entry as { id?: unknown }).id || '').trim()
        : '';
    if (id && !existingIds.has(id)) {
      return id;
    }
  }

  return null;
}

export function useTalkSessionData(
  sessionId: string,
): TalkSessionDataState & TalkSessionActions {
  const navigate = useNavigate();
  const { t } = useTranslation('session');
  const [session, setSession] = useState<TalkSessionDataState['session']>(null);
  const [accessInfo, setAccessInfo] =
    useState<TalkSessionDataState['accessInfo']>(null);
  const [loading, setLoading] = useState(true);
  const [chatSessions, setChatSessions] = useState<
    TalkSessionDataState['chatSessions']
  >([]);
  const [chatSessionsLoading, setChatSessionsLoading] = useState(true);
  const [actionLoading, setActionLoading] =
    useState<TalkSessionDataState['actionLoading']>(null);
  const [chatRefreshKey, setChatRefreshKey] = useState(0);
  const [gitState, setGitState] = useState<GitState | null>(null);
  const [gitHistoryLoading, setGitHistoryLoading] = useState(true);
  const [gitHistoryError, setGitHistoryError] = useState<string | null>(null);
  const activeChatSessionId = useTalkSessionStore(
    (state) => state.activeChatSessionId,
  );
  const setActiveChatSessionId = useTalkSessionStore(
    (state) => state.setActiveChatSessionId,
  );

  const fetchSession = useCallback(
    async (keepLoading = false) => {
      try {
        const sessionResponse = await fetch(`/api/sessions/${sessionId}`, {
          credentials: 'include',
        });
        if (!sessionResponse.ok) {
          navigate('/talk', { replace: true });
          return;
        }

        const data = await sessionResponse.json();
        const nextSession = data.data;
        setSession(nextSession);

        if (nextSession.status === 'running') {
          const accessResponse = await fetch(
            `/api/sessions/${sessionId}/access`,
            { credentials: 'include' },
          );
          if (accessResponse.ok) {
            const accessData = await accessResponse.json();
            setAccessInfo(accessData.data || null);
          }
        } else {
          setAccessInfo(null);
        }
      } catch (error) {
        console.error('Failed to fetch talk session:', error);
        toast.error(t('errors.loadTalkSession'));
      } finally {
        if (!keepLoading) setLoading(false);
      }
    },
    [navigate, sessionId, t],
  );

  const fetchChatSessions = useCallback(async () => {
    try {
      const response = await fetch(
        `/api/acpx/sessions/${sessionId}/chat-sessions`,
        { credentials: 'include' },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const sessions = (data?.sessions || []) as ChatSessionMeta[];
      const previousIds = new Set(chatSessions.map((item) => item.id));
      const fallbackCreatedSessionId =
        sessions.find((item) => !previousIds.has(item.id))?.id || null;
      const resolvedActiveSessionId = (data?.activeSessionId ||
        sessions.find((item) => item.active)?.id ||
        fallbackCreatedSessionId ||
        sessions[0]?.id ||
        null) as string | null;
      setChatSessions(sessions);
      setActiveChatSessionId(resolvedActiveSessionId);
    } catch (error) {
      console.error('Failed to load PM chat sessions:', error);
      toast.error(t('errors.loadChats'));
    } finally {
      setChatSessionsLoading(false);
    }
  }, [chatSessions, sessionId, setActiveChatSessionId, t]);

  const fetchGitHistory = useCallback(async () => {
    setGitHistoryLoading(true);
    try {
      const response = await fetch(
        `/api/acpx/sessions/${sessionId}/git-state`,
        { credentials: 'include' },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setGitState((data || null) as GitState | null);
      setGitHistoryError(null);
    } catch (error) {
      console.error('Failed to load git state:', error);
      setGitHistoryError(t('errors.loadGitState'));
    } finally {
      setGitHistoryLoading(false);
    }
  }, [sessionId, t]);

  useEffect(() => {
    void Promise.all([fetchSession(), fetchChatSessions(), fetchGitHistory()]);
  }, [fetchChatSessions, fetchGitHistory, fetchSession]);

  const bumpChatRefreshKey = useCallback(() => {
    setChatRefreshKey((value) => value + 1);
  }, []);

  const createChatSession = useCallback(async () => {
    setActionLoading('create');
    try {
      const existingIds = new Set(chatSessions.map((item) => item.id));
      const response = await fetch(
        `/api/acpx/sessions/${sessionId}/chat-sessions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name: 'New Session' }),
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      let createdChatSessionId = resolveCreatedChatSessionId(data, existingIds);

      if (createdChatSessionId) {
        const activateResponse = await fetch(
          `/api/acpx/sessions/${sessionId}/chat-sessions/${createdChatSessionId}/activate`,
          {
            method: 'POST',
            credentials: 'include',
          },
        );
        if (!activateResponse.ok)
          throw new Error(`HTTP ${activateResponse.status}`);
        setActiveChatSessionId(createdChatSessionId);
      }

      await fetchChatSessions();
      if (!createdChatSessionId) {
        createdChatSessionId =
          useTalkSessionStore.getState().activeChatSessionId;
      }
      bumpChatRefreshKey();
      return createdChatSessionId;
    } catch (error) {
      console.error('Failed to create chat session:', error);
      toast.error(t('errors.createChat'));
      return null;
    } finally {
      setActionLoading(null);
    }
  }, [
    bumpChatRefreshKey,
    chatSessions,
    fetchChatSessions,
    sessionId,
    setActiveChatSessionId,
    t,
  ]);

  const activateChatSession = useCallback(
    async (chatSessionId: string) => {
      setActionLoading(`activate:${chatSessionId}`);
      try {
        const response = await fetch(
          `/api/acpx/sessions/${sessionId}/chat-sessions/${chatSessionId}/activate`,
          {
            method: 'POST',
            credentials: 'include',
          },
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        setActiveChatSessionId(chatSessionId);
        await fetchChatSessions();
        bumpChatRefreshKey();
      } catch (error) {
        console.error('Failed to activate chat session:', error);
        toast.error(t('errors.switchChat'));
      } finally {
        setActionLoading(null);
      }
    },
    [bumpChatRefreshKey, fetchChatSessions, sessionId, t],
  );

  const deleteChatSession = useCallback(
    async (chatSessionId: string) => {
      if (!window.confirm('Delete this PM chat session?')) return;
      setActionLoading(`delete:${chatSessionId}`);
      try {
        const response = await fetch(
          `/api/acpx/sessions/${sessionId}/chat-sessions/${chatSessionId}`,
          {
            method: 'DELETE',
            credentials: 'include',
          },
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        await fetchChatSessions();
        bumpChatRefreshKey();
      } catch (error) {
        console.error('Failed to delete chat session:', error);
        toast.error(t('errors.deleteChat'));
      } finally {
        setActionLoading(null);
      }
    },
    [bumpChatRefreshKey, fetchChatSessions, sessionId, t],
  );

  const bulkDeleteChatSessions = useCallback(
    async (chatSessionIds: string[]) => {
      const targets = Array.from(new Set(chatSessionIds.filter(Boolean)));
      if (targets.length === 0) return;
      if (
        !window.confirm(
          `Delete ${targets.length} PM chat session${targets.length > 1 ? 's' : ''}?`,
        )
      )
        return;

      setActionLoading('bulk-delete');
      try {
        for (const chatSessionId of targets) {
          const response = await fetch(
            `/api/acpx/sessions/${sessionId}/chat-sessions/${chatSessionId}`,
            {
              method: 'DELETE',
              credentials: 'include',
            },
          );
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
        }
        await fetchChatSessions();
        bumpChatRefreshKey();
        toast.success(t('messages.chatsDeleted', { count: targets.length }));
      } catch (error) {
        console.error('Failed to bulk delete chat sessions:', error);
        toast.error(t('errors.deleteChats'));
      } finally {
        setActionLoading(null);
      }
    },
    [bumpChatRefreshKey, fetchChatSessions, sessionId, t],
  );

  const commitRename = useCallback(
    async (chatSession: ChatSessionMeta, nextName: string) => {
      const trimmedName = nextName.trim();
      if (!trimmedName || trimmedName === chatSession.name) return;

      setActionLoading(`rename:${chatSession.id}`);
      try {
        const response = await fetch(
          `/api/acpx/sessions/${sessionId}/chat-sessions/${chatSession.id}/rename`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ name: trimmedName }),
          },
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        await fetchChatSessions();
      } catch (error) {
        console.error('Failed to rename PM chat session:', error);
        toast.error(t('errors.renameChat'));
      } finally {
        setActionLoading(null);
      }
    },
    [fetchChatSessions, sessionId, t],
  );

  const runGitCommand = useCallback(
    async <
      T extends
        | GitSaveResponse
        | GitPublishResponse
        | GitJumpResponse
        | GitReturnResponse
        | GitBranchFromCurrentResponse,
    >(
      endpoint: string,
      loadingKey: string,
      body?: Record<string, unknown>,
    ) => {
      setActionLoading(loadingKey);
      try {
        const response = await fetch(
          `/api/acpx/sessions/${sessionId}${endpoint}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(body || {}),
          },
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(String(data?.error || `HTTP ${response.status}`));
        }

        const result = data as T;
        if (result?.state) setGitState(result.state);
        const notify = getToastMethod(String(result?.status || 'ok'));
        notify(String(result?.message || 'Git operation completed.'), {
          description:
            result?.commitMessage ||
            result?.commitHash ||
            result?.recoveryBranch ||
            undefined,
        });
        return result;
      } catch (error) {
        console.error(`Failed to run git command ${endpoint}:`, error);
        toast.error(t('errors.gitOperation'));
        return null;
      } finally {
        setActionLoading(null);
      }
    },
    [sessionId, t],
  );

  const runGitAction = useCallback(
    async (mode: 'save' | 'publish') => {
      await runGitCommand(mode === 'save' ? '/git/save' : '/git/publish', mode);
    },
    [runGitCommand],
  );

  const jumpToCommit = useCallback(
    async (commit: GitCommit) => {
      await runGitCommand('/git/jump', `jump:${commit.hash}`, {
        targetCommitHash: commit.hash,
      });
    },
    [runGitCommand],
  );

  const jumpBack = useCallback(async () => {
    await runGitCommand('/git/return', 'return');
  }, [runGitCommand]);

  return {
    session,
    accessInfo,
    loading,
    chatSessions,
    activeChatSessionId,
    chatSessionsLoading,
    actionLoading,
    chatRefreshKey,
    gitState,
    gitHistoryLoading,
    gitHistoryError,
    fetchSession,
    fetchChatSessions,
    fetchGitHistory,
    createChatSession,
    activateChatSession,
    deleteChatSession,
    bulkDeleteChatSessions,
    commitRename,
    runGitAction,
    jumpToCommit,
    jumpBack,
    bumpChatRefreshKey,
  };
}
